<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';

import TemplateCard from '@/components/Conversations/Templates/TemplateCard.vue';
import CreateTemplate from '@/components/Conversations/Templates/CreateTemplate.vue';
import EditTemplate from '@/components/Conversations/Templates/EditTemplate.vue';
import CreateTemplateButton from '@/components/Conversations/Templates/CreateTemplateButton.vue';
import SpinnerLoader from '@/components/Shared/Loaders/SpinnerLoader.vue';

import TemplateService, {
  type TemplateMutationResult,
} from '@/core/conversations/template/template.service';
import type { MessageTemplate } from '@/core/conversations/template/template.type';
import {
  type TemplateLibraryMode,
  type TemplatePreviewContext,
  type TemplatePreviewMode,
  isCustomTemplate,
} from '@/core/conversations/template/template.utils';
import { SnackbarService } from '@/core/shared/snackbar/snackbar.service';
import { ErrorService } from '@/core/shared/errors/error.service';

/**
 * The modal-agnostic template experience: browse, create, edit, delete and restore.
 * Hosted by the Conversations TemplateModal dialog and by the Talent Search
 * "Add to job" modal, each of which supplies its own shell (title, back, close) and
 * decides what happens on insert — this component never writes the shared
 * `selectedTemplate` store, so inserting here cannot leak into another composer.
 */
const props = withDefaults(
  defineProps<{
    /** `raw` highlights the tokens; `personalized` renders substituted values. */
    previewMode?: TemplatePreviewMode;
    /** Explicit personalization values — required wherever no conversation is selected. */
    previewContext?: TemplatePreviewContext;
    /** Offer immediate Send on each card (Conversations only). */
    allowSend?: boolean;
    /** Offer create/edit/delete of the user's own templates. */
    allowManage?: boolean;
  }>(),
  {
    previewMode: 'personalized',
    allowSend: true,
    allowManage: true,
  },
);

const emit = defineEmits<{
  /** The recruiter picked a template. The host decides what to do with it. */
  (e: 'insert', template: MessageTemplate): void;
  /** Immediate send of the already-personalized text. */
  (e: 'send', personalizedMessage: string): void;
  /** Lets the host drive its own title / back button / actions. */
  (e: 'mode-change', mode: TemplateLibraryMode): void;
}>();

const templateService = new TemplateService();

const mode = ref<TemplateLibraryMode>('browse');
const isLoadingTemplates = ref(false);
const loadFailed = ref(false);
const isSaving = ref(false);
// The template store is persisted and not keyed by user, so its cached contents are only
// known to belong to the current user once this session's own fetch has returned them.
// Until then nothing is rendered: otherwise the second person to sign in on a shared
// browser would read the previous user's custom templates out of localStorage.
const hasVerifiedTemplates = ref(false);
// Kept alive after leaving edit mode so the "Template was edited" undo can re-apply the
// pre-edit copy.
const templateToEdit = ref<Pick<MessageTemplate, 'id' | 'title' | 'message'> | null>(null);

const templates = computed(() => (hasVerifiedTemplates.value ? templateService.templates : []));
// Once the list has been verified for this user it stays on screen: a later refresh
// failure reports itself without blanking a list the recruiter is working from.
const showLoadingState = computed(() => !hasVerifiedTemplates.value && isLoadingTemplates.value);
const showErrorState = computed(() => !hasVerifiedTemplates.value && loadFailed.value);
const showEmptyState = computed(
  () => hasVerifiedTemplates.value && !isLoadingTemplates.value && templates.value.length === 0,
);

function setMode(next: TemplateLibraryMode) {
  if (mode.value === next) return;
  mode.value = next;
  emit('mode-change', next);
}

function findTemplate(templateId: number): MessageTemplate | undefined {
  return templates.value.find((template) => template.id === templateId);
}

function reportFailure(error: unknown, text: string) {
  ErrorService.captureException(error);
  SnackbarService.criticalAlt(text);
}

/**
 * Reports a list refresh that failed after its mutation had already succeeded. The
 * wording must stay distinct from the save-failure copy so the recruiter is never told
 * to repeat work the server has accepted.
 */
function reportRefreshFailure(result: TemplateMutationResult, text: string) {
  if (!result?.refreshError) return;
  reportFailure(result.refreshError, text);
}

/**
 * A mutation whose follow-up refresh succeeded has just fetched this user's own list, so
 * it counts as verified from here on. Without this, a create after a failed first load
 * returns to `browse` with the error state still up and the new template hidden.
 *
 * A failed refresh deliberately leaves the error state alone: the mutation is in the
 * store optimistically, but nothing has confirmed the rest of the (persisted, unkeyed)
 * cache belongs to this user — so the retry button stays, and its fetch is what verifies.
 */
function markListVerified(result: TemplateMutationResult) {
  if (result?.refreshError) return;
  hasVerifiedTemplates.value = true;
  loadFailed.value = false;
}

async function loadTemplates() {
  isLoadingTemplates.value = true;
  loadFailed.value = false;
  try {
    await templateService.fetchTemplates();
    hasVerifiedTemplates.value = true;
  } catch (error) {
    loadFailed.value = true;
    reportFailure(error, 'We could not load your templates. Please try again.');
  } finally {
    isLoadingTemplates.value = false;
  }
}

function insertTemplate(templateId: number) {
  const template = findTemplate(templateId);
  if (!template) return;
  emit('insert', template);
}

function sendTemplate(templateId: number) {
  const template = findTemplate(templateId);
  if (!template) return;
  emit(
    'send',
    templateService.mapTemplatePlaceholders(
      template.message,
      false,
      props.previewContext?.proFullName,
      props.previewContext?.jobTitle,
      props.previewContext?.proFirstName,
    ),
  );
}

async function saveTemplate(template: Pick<MessageTemplate, 'title' | 'message'>) {
  isSaving.value = true;
  try {
    const result = await templateService.createTemplate(template);
    // The template exists on the server now. Close the form whatever the follow-up
    // refresh did — leaving it open would invite a duplicate create.
    setMode('browse');
    markListVerified(result);
    reportRefreshFailure(result, 'Your template was saved, but the list is out of date.');
  } catch (error) {
    // Stay on the form with the typed content intact so the recruiter can retry.
    reportFailure(error, 'We could not save your template. Please try again.');
  } finally {
    isSaving.value = false;
  }
}

function editTemplate(templateId: number) {
  const template = findTemplate(templateId);
  if (!template) return;

  templateToEdit.value = {
    id: template.id,
    title: template.title,
    message: template.message,
  };
  setMode('edit');
}

async function updateTemplate(template: Pick<MessageTemplate, 'id' | 'title' | 'message'>) {
  isSaving.value = true;
  try {
    const result = await templateService.updateTemplate(template);
    SnackbarService.showSnackbar({
      text: 'Template was edited',
      appendIcon: 'close',
      prependIcon: 'pen',
      showUndo: true,
      horizontalPosition: 'center',
      undoAction: () => restoreEditedTemplate(),
    });
    setMode('browse');
    markListVerified(result);
    reportRefreshFailure(result, 'Your changes were saved, but the list is out of date.');
  } catch (error) {
    reportFailure(error, 'We could not save your changes. Please try again.');
  } finally {
    isSaving.value = false;
  }
}

async function restoreEditedTemplate() {
  const previousVersion = templateToEdit.value;
  if (!previousVersion) return;

  try {
    const result = await templateService.updateTemplate({
      id: previousVersion.id,
      title: previousVersion.title,
      message: previousVersion.message,
    });
    markListVerified(result);
    reportRefreshFailure(result, 'Your edit was undone, but the list is out of date.');
  } catch (error) {
    reportFailure(error, 'We could not undo your edit. Please try again.');
  }
}

async function restoreTemplate(templateId: number) {
  try {
    const result = await templateService.restoreTemplate(templateId);
    markListVerified(result);
    reportRefreshFailure(result, 'The template was restored, but the list is out of date.');
  } catch (error) {
    reportFailure(error, 'We could not restore the template. Please try again.');
  }
}

async function deleteTemplate(templateId: number) {
  try {
    const result = await templateService.deleteTemplate(templateId);
    SnackbarService.showSnackbar({
      text: 'Template deleted',
      appendIcon: 'close',
      prependIcon: 'trash',
      showUndo: true,
      horizontalPosition: 'center',
      undoAction: () => restoreTemplate(templateId),
    });
    markListVerified(result);
    reportRefreshFailure(result, 'The template was deleted, but the list is out of date.');
  } catch (error) {
    reportFailure(error, 'We could not delete the template. Please try again.');
  }
}

onMounted(() => {
  loadTemplates();
});

/**
 * Returns to the template list. Hosts call this from their own back control and when
 * closing, so a half-filled create/edit form is never what the recruiter comes back to.
 */
function showBrowse() {
  setMode('browse');
}

defineExpose({ showBrowse });
</script>

<template>
  <div data-test="template-library">
    <CreateTemplate
      v-if="mode === 'create'"
      class="mt-6"
      :submitting="isSaving"
      @cancel-creation="showBrowse"
      @save-template="saveTemplate"
    />

    <EditTemplate
      v-else-if="mode === 'edit' && templateToEdit"
      :key="templateToEdit.id"
      class="mt-6"
      :id="templateToEdit.id"
      :title="templateToEdit.title"
      :templateText="templateToEdit.message"
      :submitting="isSaving"
      @cancel-edition="showBrowse"
      @update-template="updateTemplate"
    />

    <template v-else>
      <CreateTemplateButton v-if="allowManage" class="mt-4" @create-template="setMode('create')" />

      <div
        v-if="showLoadingState"
        class="flex justify-center py-10"
        data-test="template-library-loading"
      >
        <SpinnerLoader />
      </div>

      <div
        v-else-if="showErrorState"
        class="mt-8 flex flex-col items-start gap-3"
        data-test="template-library-error"
      >
        <p class="text-sm text-shade-800">
          We could not load your templates. Your message and job selection are unaffected.
        </p>
        <button
          type="button"
          class="cursor-pointer rounded-md border-2 border-shade-880 px-3 py-1.5 text-sm font-semibold text-shade-880 transition-colors hover:bg-tint-40"
          data-test="template-library-retry"
          @click="loadTemplates"
        >
          Try again
        </button>
      </div>

      <p
        v-else-if="showEmptyState"
        class="mt-8 text-sm text-shade-800"
        data-test="template-library-empty"
      >
        You do not have any templates yet.
      </p>

      <div v-else class="templates-scrollbar">
        <TemplateCard
          v-for="template in templates"
          :key="template.id"
          :id="template.id"
          :title="template.title"
          :templateText="template.message"
          :custom="isCustomTemplate(template)"
          :preview-mode="previewMode"
          :preview-context="previewContext"
          :show-send="allowSend"
          :can-manage="allowManage"
          @insert-template="insertTemplate"
          @send-template="sendTemplate"
          @edit-template="editTemplate"
          @delete-template="deleteTemplate"
        />
      </div>
    </template>
  </div>
</template>

<style lang="postcss" scoped>
.templates-scrollbar {
  /* Height-flexible and single-column on narrow viewports so the fullscreen mobile
     modal does not clip cards or scroll sideways. */
  @apply mr-2 mt-8 grid max-h-[464px] grid-cols-1 gap-6 overflow-auto pr-4 md:grid-cols-2;

  /** Scrollbar width */
  &::-webkit-scrollbar {
    width: 8px;
  }

  /** Scrollbar color and border radius */
  &::-webkit-scrollbar-thumb {
    @apply bg-tint-80;
    border-radius: 8px;
  }
}
</style>
